import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { ROUTES } from '@utils/constants';
import LoadingSpinner from '@components/common/LoadingSpinner';
import EmailVerificationBanner from '@components/common/EmailVerificationBanner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'customer' | 'photographer' | 'admin';
  requireVerified?: boolean;
}

const EmailVerificationGate: React.FC<{ userEmail: string }> = ({ userEmail }) => {
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleResend = async () => {
    setResending(true);
    setResendStatus('idle');
    try {
      const { default: api } = await import('@/services/api');
      await api.post('/auth/resend-verification', { email: userEmail });
      setResendStatus('success');
    } catch {
      setResendStatus('error');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Email Verification Required
        </h2>
        <p className="text-gray-600 mb-2">
          You must verify your email address before accessing this page.
        </p>
        <p className="text-sm text-gray-500 mb-6">
          A verification link was sent to <strong>{userEmail}</strong>
        </p>

        {resendStatus === 'success' && (
          <p className="text-sm text-green-600 mb-4">
            Verification email sent. Check your inbox.
          </p>
        )}
        {resendStatus === 'error' && (
          <p className="text-sm text-red-600 mb-4">
            Failed to resend. Please try again.
          </p>
        )}

        <button
          onClick={handleResend}
          disabled={resending}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700
                     disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {resending ? 'Sending...' : 'Resend Verification Email'}
        </button>
      </div>
    </div>
  );
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requireVerified,
}) => {
  const { isAuthenticated, loading, user, emailVerified } = useAuth();
  const location = useLocation();
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Show loading spinner while checking authentication status
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated, but remember the intended destination
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Check role-based access if requiredRole is specified.
  // Admins are a superset: they may access any role-gated route.
  if (requiredRole && user?.role !== requiredRole && user?.role !== 'admin') {
    // Redirect based on user's actual role
    if (user?.role === 'photographer') {
      return <Navigate to="/dashboard" replace />;
    } else {
      // Customer or no role - redirect to home
      return <Navigate to={ROUTES.HOME} replace />;
    }
  }

  // Block access entirely when requireVerified=true and email is not verified
  if (requireVerified && !emailVerified && user) {
    return <EmailVerificationGate userEmail={user.email} />;
  }

  // Show email verification banner if user is not verified
  const showVerificationBanner =
    user &&
    !user.email_verified &&
    !bannerDismissed;

  return (
    <>
      {showVerificationBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
          <EmailVerificationBanner
            userEmail={user.email}
            onDismiss={() => setBannerDismissed(true)}
          />
        </div>
      )}
      <div className={showVerificationBanner ? 'pt-24' : ''}>
        {children}
      </div>
    </>
  );
};

export default ProtectedRoute;
